/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react'
import { getRequestOptions } from '../helpers/Fetchwrapper'
import { Col, Row, Table } from 'react-bootstrap-v5'
import { Link, useParams } from 'react-router-dom'
import { KTSVG } from '../../_metronic/helpers'

interface QuotationRow {
	id_sales_quotation_rows: number
	id_products: number
	unit_rate: number
	transportation_cost: number
	weight_in_kg?: number | null
	quantity: number
	original_rate: number
	discount_percent: number
	remarks: string
	delivery_location: string
	product?: {
		product_name: string
		packing: string
		discount_percent_sr?: number
		discount_percent_admin?: number
		weight_in_kg?: number | null
		item?: {
			unit?: {
				unit_name: string
			}
		}
	}
	alternate_name?: {
		alternate_name: string
	}
}

interface QuotationTerm {
	id_sales_quotation_terms: number
	clause?: string
	terms_clause?: string
}

interface QuotationProject {
	project_name?: string
	projectName?: string
	show_transportation_cost?: string
	showTransportationCost?: string
	show_effective_rate?: string
	showEffectiveRate?: string
	show_sr_max_discount?: string
	showSrMaxDiscount?: string
	show_admin_max_discount?: string
	showAdminMaxDiscount?: string
	show_weight?: string
	showWeight?: string
}

interface QuotationInfo {
	id_sales_quotations: number
	id_projects: number
	id_leads: number
	quotation_date: string
	reference_number: string
	quotation_text: string
	quotation_footer: string
	project?: QuotationProject
	lead?: {
		lead_name: string
	}
	rows: QuotationRow[]
	terms: QuotationTerm[]
}

interface RouteParams {
	idSalesQuotations: string
}

const numberFormat = (value: any) => {
	const parsed = parseFloat(value)
	if (Number.isNaN(parsed)) {
		return value ?? ''
	}
	return parsed.toLocaleString('en-US', {
		style: 'decimal',
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})
}

const getProductName = (row: QuotationRow) => {
	const alternateName = row.alternate_name?.alternate_name
	const productName = row.product?.product_name ?? ''
	const packing = row.product?.packing ?? ''
	const unitName = row.product?.item?.unit?.unit_name ?? ''

	const name = alternateName && alternateName.trim() !== '' ? alternateName : productName
	const packLabel = packing !== '' && unitName !== '' ? ` (${packing} ${unitName})` : ''

	return `${name}${packLabel}`
}

const getRowAmount = (row: QuotationRow) => {
	const quantity = parseFloat(String(row.quantity ?? 0)) || 0
	const rate = parseFloat(String(row.unit_rate ?? 0)) || 0
	const transportationCost = parseFloat(String(row.transportation_cost ?? 0)) || 0

	return quantity * (rate + transportationCost)
}

const getProjectFlag = (project: QuotationProject | undefined, camelKey: string, snakeKey: string) => {
	if (!project) return undefined
	const projectAny = project as Record<string, string | undefined>
	return projectAny[camelKey] ?? projectAny[snakeKey]
}

export default function SalesQuotationDetails() {
	const { idSalesQuotations }: RouteParams = useParams()
	const [quotationInfo, setQuotationInfo] = useState<QuotationInfo | null>(null)
	const [loading, setLoading] = useState(true)

	useEffect(() => {
		if (idSalesQuotations) {
			getQuotationInfo()
		}
	}, [idSalesQuotations])

	const getQuotationInfo = () => {
		setLoading(true)
		fetch(`${process.env.REACT_APP_API_URL}quotation/${idSalesQuotations}`, getRequestOptions())
			.then((resp) => resp.json())
			.then((resp) => {
				setQuotationInfo(resp.quotation ?? null)
			})
			.catch((error) => {
				console.log(error, 'catch the hoop')
			})
			.finally(() => {
				setLoading(false)
			})
	}

	if (loading) {
		return <div className='card card-custom p-10'>Loading quotation details...</div>
	}

	if (!quotationInfo) {
		return <div className='card card-custom p-10'>Quotation not found.</div>
	}

	const project = quotationInfo.project
	const showTransportationCost = getProjectFlag(project, 'showTransportationCost', 'show_transportation_cost') !== 'No'
	const showEffectiveRate = getProjectFlag(project, 'showEffectiveRate', 'show_effective_rate') !== 'No'
	const showSrMaxDiscount = getProjectFlag(project, 'showSrMaxDiscount', 'show_sr_max_discount') !== 'No'
	const showAdminMaxDiscount = getProjectFlag(project, 'showAdminMaxDiscount', 'show_admin_max_discount') !== 'No'
	const showWeight = getProjectFlag(project, 'showWeight', 'show_weight') === 'Yes'

	const totalAmount = quotationInfo.rows?.reduce((sum, row) => sum + getRowAmount(row), 0) ?? 0

	// Before Amount: SL, Product, Delivery, Rate, [Transport], [Effective], Qty, [Weight]
	const colsBeforeAmount =
		5 + (showTransportationCost ? 1 : 0) + (showEffectiveRate ? 1 : 0) + (showWeight ? 1 : 0)

	// After Amount: [SR], [Admin], Discount, Remarks
	const colsAfterAmount = 2 + (showSrMaxDiscount ? 1 : 0) + (showAdminMaxDiscount ? 1 : 0)

	const projectName = project?.projectName ?? project?.project_name ?? ''

	return (
		<>
			<div className='card card-custom mb-5'>
				<div className='card-header'>
					<div className='card-title'>
						<KTSVG path='/media/svg/icons/Layout/Layout-grid.svg' className='svg-icon-1 svg-icon-primary' />
						<span className='text fw-bolder' style={{ marginLeft: '10px' }}>
							Sales Quotation Details
						</span>
					</div>
					<div className='card-toolbar'>
						<Link to='/sales-quotations' className='btn btn-sm btn-light-primary me-2'>
							Back to List
						</Link>
						<Link
							to={`/sales-quotation-create?idSalesQuotations=${quotationInfo.id_sales_quotations}`}
							className='btn btn-sm btn-light-primary me-2'
						>
							<KTSVG path='/media/svg/icons/Design/Edit.svg' className='svg-icon-5 svg-icon' /> Edit
						</Link>
						<a
							className='btn btn-sm btn-light-primary'
							target='_blank'
							rel='noopener noreferrer'
							href={`${process.env.REACT_APP_BACKEND}quotationDownload/${quotationInfo.id_sales_quotations}`}
						>
							<KTSVG path='/media/svg/icons/Files/Downloads-folder.svg' className='svg-icon-5 svg-icon' /> Download
						</a>
					</div>
				</div>
			</div>

			<div className='card card-custom mb-5'>
				<div className='card-body'>
					<Row className='mb-4'>
						<Col md={6}>
							<div>
								<strong>Project:</strong> {projectName}
							</div>
							<div>
								<strong>Lead:</strong> {quotationInfo.lead?.lead_name ?? ''}
							</div>
						</Col>
						<Col md={6}>
							<div>
								<strong>Date:</strong> {quotationInfo.quotation_date ?? ''}
							</div>
							<div>
								<strong>Reference No:</strong> {quotationInfo.reference_number ?? ''}
							</div>
						</Col>
					</Row>

					<div className='mb-5'>
						<h5 className='mb-3'>Quotation Header</h5>
						<div
							className='border rounded p-4 bg-light'
							dangerouslySetInnerHTML={{ __html: quotationInfo.quotation_text ?? '' }}
						/>
					</div>

					<h5 className='mb-3'>Products</h5>
					<div className='table-responsive'>
						<Table bordered hover size='sm'>
							<thead>
								<tr>
									<th className='text-center'>SL</th>
									<th>Product</th>
									<th>Delivery Location</th>
									<th className='text-end'>Rate (Per Unit)</th>
									{showTransportationCost && <th className='text-end'>Transportation Cost (Per Unit)</th>}
									{showEffectiveRate && <th className='text-end'>Effective Rate (Rate + Transportation Cost)</th>}
									<th className='text-end'>Quantity</th>
									{showWeight && <th className='text-end'>Weight (KG)</th>}
									<th className='text-end'>Amount</th>
									{showSrMaxDiscount && <th className='text-end'>SR Max Discount (%)</th>}
									{showAdminMaxDiscount && <th className='text-end'>Admin Max Discount (%)</th>}
									<th className='text-end'>Discount (%)</th>
									<th>Remarks</th>
								</tr>
							</thead>
							<tbody>
								{quotationInfo.rows?.map((row, index) => {
									const rate = parseFloat(String(row.unit_rate ?? 0)) || 0
									const transportationCost = parseFloat(String(row.transportation_cost ?? 0)) || 0
									const effectiveRate = rate + transportationCost
									const weight = row.weight_in_kg ?? row.product?.weight_in_kg

									return (
										<tr key={row.id_sales_quotation_rows ?? index}>
											<td className='text-center'>{index + 1}</td>
											<td>{getProductName(row)}</td>
											<td>{row.delivery_location ?? ''}</td>
											<td className='text-end'>{numberFormat(rate)}</td>
											{showTransportationCost && (
												<td className='text-end'>{numberFormat(transportationCost)}</td>
											)}
											{showEffectiveRate && <td className='text-end'>{numberFormat(effectiveRate)}</td>}
											<td className='text-end'>{numberFormat(row.quantity)}</td>
											{showWeight && (
												<td className='text-end'>
													{weight != null && weight !== ('' as any) ? numberFormat(weight) : '-'}
												</td>
											)}
											<td className='text-end'>{numberFormat(getRowAmount(row))}</td>
											{showSrMaxDiscount && (
												<td className='text-end'>{numberFormat(row.product?.discount_percent_sr ?? 0)}</td>
											)}
											{showAdminMaxDiscount && (
												<td className='text-end'>{numberFormat(row.product?.discount_percent_admin ?? 0)}</td>
											)}
											<td className='text-end'>{numberFormat(row.discount_percent)}</td>
											<td>{row.remarks ?? ''}</td>
										</tr>
									)
								})}
								<tr>
									<td colSpan={colsBeforeAmount} className='text-end'>
										<strong>Total Amount</strong>
									</td>
									<td className='text-end'>
										<strong>{numberFormat(totalAmount)}</strong>
									</td>
									<td colSpan={colsAfterAmount}></td>
								</tr>
							</tbody>
						</Table>
					</div>

					<h5 className='mb-3 mt-5'>Business Terms & Conditions</h5>
					<Table responsive bordered hover size='sm'>
						<thead>
							<tr>
								<th className='text-center' style={{ width: '10%' }}>
									SL
								</th>
								<th>Clause</th>
							</tr>
						</thead>
						<tbody>
							{(quotationInfo.terms ?? []).map((term, index) => (
								<tr key={term.id_sales_quotation_terms ?? index}>
									<td className='text-center'>{index + 1}</td>
									<td>{term.clause ?? term.terms_clause ?? ''}</td>
								</tr>
							))}
							{(quotationInfo.terms ?? []).length === 0 && (
								<tr>
									<td colSpan={2} className='text-center'>
										No terms added
									</td>
								</tr>
							)}
						</tbody>
					</Table>

					<div className='mt-5'>
						<h5 className='mb-3'>Quotation Footer</h5>
						<div
							className='border rounded p-4 bg-light'
							dangerouslySetInnerHTML={{ __html: quotationInfo.quotation_footer ?? '' }}
						/>
					</div>
				</div>
			</div>
		</>
	)
}
